// The tree describes rules for future descendants as well as today's catalog.
// Product exceptions carry their parent IDs even when their page is still closed.
export const LEVELS = [
  { kind: "family", group: "families" },
  { kind: "subfamily", group: "subfamilies", parent: "family" },
  { kind: "category", group: "categories", parent: "subfamily" },
  { kind: "subcategory", group: "subcategories", parent: "category" },
  { kind: "item", group: "products" },
];
const keyOf = (kind, id) => kind + ":" + id;
const ROOT = { key: "root", path: [] };
const isTreeRule = (rule) => Number(rule.policy_version) === 2;
export const scopeOf = (rule) => LEVELS.find(({ kind }) => rule[kind + "_id"] != null)?.kind;
export const retiredProduct = (node) => node.kind === "item" &&
  (node.record.is_active === false || node.record.merged_into_item_id != null);
const ruleKey = (rule) => {
  const kind = scopeOf(rule);
  return kind ? keyOf(kind, rule[kind + "_id"]) : "root";
};

export function serializeRule(rule) {
  const clean = { rule_type: rule.rule_type };
  if (isTreeRule(rule)) clean.policy_version = 2;
  for (const { kind } of LEVELS) {
    const field = kind + "_id";
    if (rule[field] != null) clean[field] = Number(rule[field]);
  }
  return clean;
}

export function buildAccessTree(catalog, rules) {
  const nodes = new Map();
  const children = new Map();
  const makeNode = (kind, record, parentKey) => {
    const parent = nodes.get(parentKey);
    const key = keyOf(kind, record.id);
    return { kind, record, key, path: [...(parent?.path || []), key], parentKey };
  };
  const add = (node) => {
    nodes.set(node.key, node);
    if (!children.has(node.parentKey)) children.set(node.parentKey, []);
    children.get(node.parentKey).push(node);
  };
  for (const { kind, group, parent } of LEVELS.slice(0, 4)) {
    for (const record of catalog?.[group] || []) {
      const parentKey = parent ? keyOf(parent, record[parent + "_id"]) : "root";
      if (record.is_active !== false && (!parent || nodes.has(parentKey))) add(makeNode(kind, record, parentKey));
    }
  }
  const itemNode = (item) => makeNode("item", item, item.subcategory_id != null
    ? keyOf("subcategory", item.subcategory_id) : keyOf("category", item.category_id));
  for (const rule of rules) {
    if (rule.item_id != null && rule.item && !nodes.has(ruleKey(rule))) add(itemNode(rule.item));
  }

  const specific = new Map();
  const included = new Map();
  const excluded = new Set();
  let unsupported = false;
  let hasLegacy = false;
  for (const rule of rules) {
    const kind = scopeOf(rule);
    const key = ruleKey(rule);
    const node = nodes.get(key);
    if (kind && (!node || (kind === "item" && !nodes.has(node.parentKey) && !retiredProduct(node)))) unsupported = true;
    if (LEVELS.filter((level) => rule[level.kind + "_id"] != null).length > 1) unsupported = true;
    if (isTreeRule(rule)) {
      if (!["allow", "deny"].includes(rule.rule_type) || specific.has(key)) unsupported = true;
      specific.set(key, rule.rule_type);
    } else if ((rule.policy_version == null || Number(rule.policy_version) === 1) && rule.rule_type === "all" && !kind) {
      hasLegacy = true;
    } else if ((rule.policy_version == null || Number(rule.policy_version) === 1) && kind && kind !== "item" && ["only", "all_except"].includes(rule.rule_type)) {
      hasLegacy = true;
      if (rule.rule_type === "only") {
        if (!included.has(kind)) included.set(kind, new Set());
        included.get(kind).add(key);
      } else excluded.add(key);
    } else unsupported = true;
  }
  if (specific.has("root") && hasLegacy) unsupported = true;
  const legacyBase = included.size > 0 || excluded.size > 0;
  function decision(path, inheritedKey) {
    for (let index = path.length - 1; index >= 0; index -= 1) {
      if (path[index] !== inheritedKey && specific.has(path[index])) return { allow: specific.get(path[index]) === "allow", source: path[index] };
    }
    if (specific.has("root")) return { allow: specific.get("root") === "allow", source: "root" };
    const allow = !path.some((key) => excluded.has(key)) && [...included.values()].every((group) => path.some((key) => group.has(key)));
    return { allow, source: legacyBase ? "legacy" : "root" };
  }
  // Compute mixed state from all taxonomy branches and every stored exception.
  // Loaded product pages are irrelevant: any other product inherits the branch.
  const states = new Map();
  function getState(node) {
    if (states.has(node.key)) return states.get(node.key);
    const current = decision(node.path);
    const mixed = (children.get(node.key) || []).some((child) => {
      const state = getState(child);
      return state.mixed || state.allow !== current.allow;
    });
    const result = { ...current, mixed };
    states.set(node.key, result);
    return result;
  }
  return { nodes, children, itemNode, decision, getState, specific, legacyBase, unsupported };
}

function treeRule(node, allow) {
  const rule = { policy_version: 2, rule_type: allow ? "allow" : "deny" };
  if (node.kind) rule[node.kind + "_id"] = Number(node.record.id);
  if (node.kind === "item") rule.item = node.record;
  return rule;
}

export function setBranch(rules, tree, node, allow) {
  // Selecting a whole branch clears its explicit descendant exceptions, including
  // item rules on pages that have never been opened.
  const next = rules.filter((rule) => !isTreeRule(rule) || !tree.nodes.get(ruleKey(rule))?.path.includes(node.key));
  return [...next, treeRule(node, allow)];
}

// Each branch also represents a future child without an explicit rule. Only
// products mentioned in either policy can behave differently from their branch.
export function delegationLimits(tree, ceiling) {
  const missing = new Set(), outside = new Set(), blockedInheritance = new Set();
  let restricted = false;
  const nodes = new Map([...tree.nodes, ...ceiling.nodes]);
  for (const node of [ROOT, ...nodes.values()]) {
    const granted = tree.decision(node.path).allow;
    const permitted = ceiling.decision(node.path).allow;
    if (!permitted) restricted = true;
    if (granted !== permitted) {
      const group = permitted ? missing : outside;
      group.add("root");
      for (const key of node.path) group.add(key);
    }
    if (!permitted && !granted) {
      for (const key of node.path) {
        if (tree.specific.get(key) === "deny" && tree.decision(node.path, key).allow) blockedInheritance.add(key);
      }
    }
  }
  return { restricted, outside: outside.has("root"), blockedInheritance,
    canAdd: node => node.kind === "item" ? ceiling.decision(node.path).allow && !tree.decision(node.path).allow : missing.has(node.key) };
}

// Copy the decision for future children, then just the exceptions below it.
// The editor can do this without opening the branch or loading product pages.
export function copyDelegableBranch(rules, tree, ceiling, node = ROOT) {
  const allow = ceiling.decision(node.path).allow;
  const next = node === ROOT ? [treeRule(ROOT, allow)] : setBranch(rules, tree, node, allow);
  if (node.kind === "item") return next;
  for (const descendant of ceiling.nodes.values()) {
    if (node !== ROOT && (descendant.key === node.key || !descendant.path.includes(node.key))) continue;
    const allowed = ceiling.decision(descendant.path).allow;
    const inherited = ceiling.decision(descendant.path.slice(0, -1)).allow;
    if (allowed !== inherited) next.push(treeRule(descendant, allowed));
  }
  return next;
}

export const inheritBranch = (rules, node) => rules.filter((rule) => !isTreeRule(rule) || ruleKey(rule) !== node.key);

function ruleGroups(rules, tree) {
  const groups = new Map();
  for (const rule of rules) {
    const kind = scopeOf(rule);
    if (!kind) continue;
    const group = LEVELS.find((level) => level.kind === kind).group;
    const key = group + "-" + rule.rule_type;
    if (!groups.has(key)) groups.set(key, { key, group, mode: rule.rule_type, names: [] });
    groups.get(key).names.push(tree.nodes.get(ruleKey(rule))?.record.name || "#" + rule[kind + "_id"]);
  }
  return [...groups.values()];
}

export function describePolicy(rules, tree, words) {
  const base = tree.legacyBase ? words.priorBase : tree.specific.get("root") === "deny" ? words.baseNone : words.baseAll;
  const groups = ruleGroups(rules, tree).map((group) => ({ ...group,
    title: words[group.group] + " · " + words.ruleLabels[group.mode] + " (" + group.names.length + ")",
  }));
  const preview = groups.length ? [base, ...groups.map((group) => group.title)].join(" · ")
    : tree.specific.get("root") === "deny" ? words.noItems : words.allItems;
  return { base, groups, preview };
}
